import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import './HOFranchiseeCharges.css';

function HOFranchiseeCharges({ onClose }) {
  const [franchisees, setFranchisees] = useState([]);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Filter
  const [filterFranchisee, setFilterFranchisee] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending');
  
  // New charge form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCharge, setNewCharge] = useState({
    franchiseeId: '',
    type: 'courier', // 'courier' or 'other'
    amount: '',
    description: '',
    month: new Date().getMonth(),
    year: new Date().getFullYear()
  });

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load franchisees
      const franchiseesSnapshot = await getDocs(collection(db, 'Franchisees'));
      const franchiseesList = franchiseesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      franchiseesList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setFranchisees(franchiseesList);

      // Load all charges
      const chargesSnapshot = await getDocs(
        query(collection(db, 'franchiseeCharges'), orderBy('createdAt', 'desc'))
      );
      const chargesList = chargesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCharges(chargesList);

    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCharge = async () => {
    if (!newCharge.franchiseeId || !newCharge.amount || !newCharge.description) {
      alert('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      const franchisee = franchisees.find(f => f.id === newCharge.franchiseeId);
      
      await addDoc(collection(db, 'franchiseeCharges'), {
        franchiseeId: newCharge.franchiseeId,
        franchiseeName: franchisee?.name || 'Unknown',
        type: newCharge.type,
        amount: parseFloat(newCharge.amount),
        description: newCharge.description,
        month: newCharge.month,
        year: newCharge.year,
        monthName: monthNames[newCharge.month],
        status: 'pending', // pending, included, removed
        createdAt: Timestamp.now(),
        createdBy: 'HO'
      });

      alert('Charge added successfully!');
      setShowAddForm(false);
      setNewCharge({
        franchiseeId: '',
        type: 'courier',
        amount: '',
        description: '',
        month: new Date().getMonth(),
        year: new Date().getFullYear()
      });
      loadData();
    } catch (error) {
      console.error('Error adding charge:', error);
      alert('Error adding charge');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    if (!window.confirm('Are you sure you want to delete this charge?')) return;

    try {
      await deleteDoc(doc(db, 'franchiseeCharges', chargeId));
      loadData();
    } catch (error) {
      console.error('Error deleting charge:', error);
      alert('Error deleting charge');
    }
  };

  // Filter charges
  const filteredCharges = charges.filter(charge => {
    if (filterFranchisee !== 'all' && charge.franchiseeId !== filterFranchisee) return false;
    if (filterType !== 'all' && charge.type !== filterType) return false;
    if (filterStatus !== 'all' && charge.status !== filterStatus) return false;
    return true;
  });

  // Calculate totals
  const totals = filteredCharges.reduce((acc, charge) => {
    acc.total += charge.amount || 0;
    if (charge.type === 'courier') acc.courier += charge.amount || 0;
    if (charge.type === 'other') acc.other += charge.amount || 0;
    return acc;
  }, { total: 0, courier: 0, other: 0 });

  if (loading) {
    return <div className="ho-charges-loading">Loading charges...</div>;
  }

  return (
    <div className="ho-charges-container">
      <div className="ho-charges-header">
        <div>
          <h1>💰 Franchisee Charges</h1>
          <p>Add courier fees and other charges to franchisee month-ends</p>
        </div>
        <button onClick={onClose} className="back-btn">← Back to Dashboard</button>
      </div>

      {/* Add New Charge Button */}
      <div className="add-charge-section">
        {!showAddForm ? (
          <button onClick={() => setShowAddForm(true)} className="btn-add-charge">
            + Add New Charge
          </button>
        ) : (
          <div className="add-charge-form">
            <h3>Add New Charge</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Franchisee:</label>
                <select
                  value={newCharge.franchiseeId}
                  onChange={(e) => setNewCharge({...newCharge, franchiseeId: e.target.value})}
                >
                  <option value="">Select Franchisee</option>
                  {franchisees.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Charge Type:</label>
                <select
                  value={newCharge.type}
                  onChange={(e) => setNewCharge({...newCharge, type: e.target.value})}
                >
                  <option value="courier">Courier Fees</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount (R):</label>
                <input
                  type="number"
                  value={newCharge.amount}
                  onChange={(e) => setNewCharge({...newCharge, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label>Month-End:</label>
                <div className="month-year-select">
                  <select
                    value={newCharge.month}
                    onChange={(e) => setNewCharge({...newCharge, month: parseInt(e.target.value)})}
                  >
                    {monthNames.map((month, idx) => (
                      <option key={idx} value={idx}>{month}</option>
                    ))}
                  </select>
                  <select
                    value={newCharge.year}
                    onChange={(e) => setNewCharge({...newCharge, year: parseInt(e.target.value)})}
                  >
                    {[2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Description:</label>
                <input
                  type="text"
                  value={newCharge.description}
                  onChange={(e) => setNewCharge({...newCharge, description: e.target.value})}
                  placeholder="e.g., T-shirt order delivery - 15 Nov"
                />
              </div>
            </div>

            <div className="form-actions">
              <button onClick={handleAddCharge} className="btn-save" disabled={saving}>
                {saving ? 'Saving...' : '💾 Save Charge'}
              </button>
              <button onClick={() => setShowAddForm(false)} className="btn-cancel">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Franchisee:</label>
          <select value={filterFranchisee} onChange={(e) => setFilterFranchisee(e.target.value)}>
            <option value="all">All Franchisees</option>
            {franchisees.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="courier">Courier Fees</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="included">Included in SOD</option>
            <option value="removed">Removed by Franchisee</option>
          </select>
        </div>
      </div>

      {/* Summary */}
      <div className="summary-bar">
        <div className="summary-item">
          <span className="label">Total Charges:</span>
          <span className="value">{filteredCharges.length}</span>
        </div>
        <div className="summary-item">
          <span className="label">Courier:</span>
          <span className="value">R {totals.courier.toFixed(2)}</span>
        </div>
        <div className="summary-item">
          <span className="label">Other:</span>
          <span className="value">R {totals.other.toFixed(2)}</span>
        </div>
        <div className="summary-item highlight">
          <span className="label">Total:</span>
          <span className="value">R {totals.total.toFixed(2)}</span>
        </div>
      </div>

      {/* Charges Table */}
      {filteredCharges.length === 0 ? (
        <div className="no-charges">
          <p>No charges found. Click "Add New Charge" to add courier or other fees.</p>
        </div>
      ) : (
        <div className="charges-table-container">
          <table className="charges-table">
            <thead>
              <tr>
                <th>Franchisee</th>
                <th>Type</th>
                <th>Description</th>
                <th>Month-End</th>
                <th className="amount">Amount</th>
                <th>Status</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCharges.map(charge => (
                <tr key={charge.id} className={`status-${charge.status}`}>
                  <td className="franchisee-name">{charge.franchiseeName}</td>
                  <td>
                    <span className={`type-badge ${charge.type}`}>
                      {charge.type === 'courier' ? '📦 Courier' : '📝 Other'}
                    </span>
                  </td>
                  <td className="description">{charge.description}</td>
                  <td>{charge.monthName} {charge.year}</td>
                  <td className="amount">R {(charge.amount || 0).toFixed(2)}</td>
                  <td>
                    <span className={`status-badge ${charge.status}`}>
                      {charge.status === 'pending' && '⏳ Pending'}
                      {charge.status === 'included' && '✅ Included'}
                      {charge.status === 'removed' && '❌ Removed'}
                    </span>
                  </td>
                  <td className="date">
                    {charge.createdAt?.toDate?.().toLocaleDateString() || '-'}
                  </td>
                  <td className="actions">
                    <button 
                      onClick={() => handleDeleteCharge(charge.id)} 
                      className="btn-delete"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default HOFranchiseeCharges;